import { useLazyQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { SEARCH_CASES } from '@core/graphql/queries/searchCases';
import { track } from '@core/logging';
import { useMemo, useCallback } from 'react';

import type { CaseSearchFilters, CaseSearchResponse } from '../domain/types';

interface CaseSearchQueryResponse {
  searchCases: CaseSearchResponse;
}

export function useCaseSearch() {
  const [executeSearch, { data, loading, error }] = useLazyQuery<CaseSearchQueryResponse>(SEARCH_CASES, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    track('CaseSearchFailed', {
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    return transformErrors(error);
  }, [error]);

  const searchResult: Array<Record<string, unknown>> = useMemo(() => {
    if (data?.searchCases?.results) {
      track('CaseSearchSucceeded', {
        resultCount: data.searchCases.results.length.toString(),
      });

      // Extract data field from each result item
      return data.searchCases.results.map(item => item.data as Record<string, unknown>);
    }

    return [];
  }, [data]);

  const search = useCallback(
    async (filters: CaseSearchFilters) => {
      track('CaseSearchInitiated');

      await executeSearch({
        variables: {
          input: {
            searchType: filters.searchType,
            searchValue: filters.searchValue,
            userType: filters.userType,
            communityId: filters.communityId,
          },
        },
      });
    },
    [executeSearch]
  );

  return {
    searchResult,
    isSearching: loading,
    errors,
    search,
  };
}
